import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Container,
  CssBaseline,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Link,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [forgotPasswordDialog, setForgotPasswordDialog] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPasswordForgot, setNewPasswordForgot] = useState("");
  const [confirmPasswordForgot, setConfirmPasswordForgot] = useState("");

  const {
    login,
    forgotPassword,
    confirmForgotPassword,
    encryptPassword,
    forcePasswordChange: forcePasswordChangeFunc,
  } = useAuth();
  const navigate = useNavigate();

  // Helper function for Cognito error messages
  const getCognitoErrorMessage = (error: any): string => {
    if (!error.message) {
      return "An unexpected error occurred. Please try again.";
    }

    const errorMap: { [key: string]: string } = {
      UserNotFoundException:
        "User not found. Please check your username/email.",
      NotAuthorizedException: "Incorrect password. Please try again.",
      UserNotConfirmedException:
        "Please confirm your email address before logging in.",
      PasswordResetRequiredException:
        "Your password needs to be reset. Please use the 'Forgot Password' feature.",
      InvalidParameterException:
        "Invalid input parameters. Please check your entries.",
      CodeMismatchException:
        "Invalid verification code. Please check the code and try again.",
      ExpiredCodeException:
        "Verification code has expired. Please request a new one.",
      LimitExceededException:
        "Too many attempts. Please wait a few minutes and try again.",
      InvalidPasswordException:
        "Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, numbers, and symbols.",
      UsernameExistsException:
        "Username already exists. Please choose a different one.",
      MFA_REQUIRED: "Multi-factor authentication required.",
      TOTP_REQUIRED: "Time-based one-time password required.",
      CUSTOM_CHALLENGE: "Additional authentication required.",
    };

    for (const [key, message] of Object.entries(errorMap)) {
      if (error.message.includes(key) || error.code === key) {
        return message;
      }
    }

    return error.message || "An unexpected error occurred. Please try again.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!username || !password) {
      setError("Please enter both username and password");
      setLoading(false);
      return;
    }

    try {
      // Encrypt the password before sending (for your records)
      const encryptedPassword = encryptPassword(password);

      // Login using SRP - password is NOT sent over the network
      await login(username, password, encryptedPassword);
      navigate("/dashboard");
    } catch (error: any) {
      if (error.code === "NEW_PASSWORD_REQUIRED") {
        setForcePasswordChange(true);
      } else {
        const errorMessage = getCognitoErrorMessage(error);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForcePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please enter both new password and confirmation");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      // Encrypt the new password for your records
      const encryptedNewPassword = encryptPassword(newPassword);

      await forcePasswordChangeFunc(newPassword, encryptedNewPassword);
      setForcePasswordChange(false);
      setNewPassword("");
      setConfirmPassword("");
      navigate("/dashboard");
    } catch (error: any) {
      const errorMessage = getCognitoErrorMessage(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // const handleHomeClick = () => {
  //   window.location.href = "/";
  // };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setForgotPasswordLoading(true);

    if (!forgotPasswordEmail) {
      setError("Please enter your email address");
      setForgotPasswordLoading(false);
      return;
    }

    try {
      await forgotPassword(forgotPasswordEmail);
      setForgotPasswordStep(1);
      setError("");
    } catch (error: any) {
      const errorMessage = getCognitoErrorMessage(error);
      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleConfirmNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPasswordForgot || !confirmPasswordForgot) {
      setError("Please enter both new password and confirmation");
      return;
    }

    if (newPasswordForgot !== confirmPasswordForgot) {
      setError("Passwords do not match");
      return;
    }

    if (newPasswordForgot.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setForgotPasswordLoading(true);

    try {
      // Encrypt the new password for your records
      const encryptedNewPassword = encryptPassword(newPasswordForgot);

      await confirmForgotPassword(
        forgotPasswordEmail,
        verificationCode,
        newPasswordForgot,
        encryptedNewPassword
      );
      setForgotPasswordDialog(false);
      setForgotPasswordStep(0);
      setForgotPasswordEmail("");
      setVerificationCode("");
      setNewPasswordForgot("");
      setConfirmPasswordForgot("");
      setError(
        "Password reset successfully! You can now login with your new password."
      );
    } catch (error: any) {
      const errorMessage = getCognitoErrorMessage(error);
      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const resetForgotPasswordFlow = () => {
    setForgotPasswordDialog(false);
    setForgotPasswordStep(0);
    setForgotPasswordEmail("");
    setVerificationCode("");
    setNewPasswordForgot("");
    setConfirmPasswordForgot("");
    setError("");
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordDialog}
        onClose={resetForgotPasswordFlow}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VpnKeyIcon />
            <Typography variant="h6">Reset Your Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={forgotPasswordStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Enter Email</StepLabel>
            </Step>
            <Step>
              <StepLabel>Enter Code</StepLabel>
            </Step>
            <Step>
              <StepLabel>New Password</StepLabel>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {forgotPasswordStep === 0 && (
            <Box component="form" onSubmit={handleForgotPassword}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Enter your username or email to receive a verification code
              </Typography>
              <TextField
                fullWidth
                label="Username or Email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                disabled={forgotPasswordLoading}
                margin="normal"
                required
              />
            </Box>
          )}

          {forgotPasswordStep === 1 && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Enter the verification code sent to your email
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={forgotPasswordLoading}
                margin="normal"
                required
              />
            </Box>
          )}

          {forgotPasswordStep === 2 && (
            <Box component="form" onSubmit={handleConfirmNewPassword}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Enter your new password
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPasswordForgot}
                onChange={(e) => setNewPasswordForgot(e.target.value)}
                disabled={forgotPasswordLoading}
                margin="normal"
                required
                helperText="Password must be at least 8 characters with uppercase, lowercase, numbers, and symbols"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPasswordForgot}
                onChange={(e) => setConfirmPasswordForgot(e.target.value)}
                disabled={forgotPasswordLoading}
                margin="normal"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetForgotPasswordFlow}
            disabled={forgotPasswordLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={
              forgotPasswordStep === 0
                ? handleForgotPassword
                : forgotPasswordStep === 2
                ? handleConfirmNewPassword
                : () => setForgotPasswordStep(2)
            }
            variant="contained"
            disabled={forgotPasswordLoading}
          >
            {forgotPasswordLoading
              ? "Processing..."
              : forgotPasswordStep === 0
              ? "Send Code"
              : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Password Change Dialog */}
      <Dialog
        open={forcePasswordChange}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VpnKeyIcon />
            <Typography variant="h6">Change Your Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            You must change your password before proceeding.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleForcePasswordChange}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              margin="normal"
              required
              helperText="Password must be at least 8 characters with uppercase, lowercase, numbers, and symbols"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleForcePasswordChange}
            variant="contained"
            disabled={loading}
            fullWidth
          >
            {loading ? "Changing Password..." : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Login Form */}
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* <div className="hidden lg:flex items-center space-x-6">
          <button
            onClick={handleHomeClick}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-green text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          > */}
        {/* <LogIn className="h-4 w-4" /> */}
        {/* <span>Dashboard</span>
          </button>
        </div> */}

        <Avatar sx={{ m: 1, bgcolor: "#919749" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Welcome to Almond Tree Dashboard
        </Typography>
        <Paper elevation={3} sx={{ mt: 3, p: 4, width: "100%" }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && !forcePasswordChange && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ backgroundColor: "#919749", mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setForgotPasswordDialog(true)}
                sx={{ cursor: "pointer" }}
              >
                Forgot your password?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
